import { TwoUIProgressView } from "@amx/ux-twoui-telemetry";
import { useTheme } from "@amx/ux-twoui-theming";
import { ChoiceGroup as FluentChoiceGroup, IChoiceGroupOption, IChoiceGroupProps, Label } from "@fluentui/react";
import React, { useEffect, useState } from "react";
import { useComponentLogger } from "../../shared/logging";
import { validateProps } from "../../shared/props";
import { isMobileViewport, useViewport } from "../../styles";
import { ErrorCallout } from "../ErrorCallout/ErrorCallout";
import { UpdatingLoadingSpinner } from "../Spinner/Spinner";
import { Stack } from "../Stacks/Stack/Stack";
import { getContainerStyles } from "./ChoiceGroup.styles";
import { ChoiceGroupBaseProps, ChoiceGroupOptionBaseProps, ChoiceGroupOptionProps } from "./shared/props";
import { getChoiceGroupOptionStyles } from "./shared/styles";
import { MediaButton, MediaButtonChoiceGroupProps } from "./variants/MediaButtonChoiceGroup/MediaButtonChoiceGroup";
import { SelectButton, SelectButtonChoiceGroupProps } from "./variants/SelectButtonChoiceGroup/SelectButtonChoiceGroup";

export interface ChoiceGroupBasicProps extends ChoiceGroupBaseProps {
    readonly optionType: "basic";

    readonly options: readonly ChoiceGroupOptionBaseProps[];
}

export type ChoiceGroupProps = ChoiceGroupBasicProps | SelectButtonChoiceGroupProps | MediaButtonChoiceGroupProps;

//optionType, options, height and mediaPosition are not deconstructed in order to avoid a TS bug (https://github.com/microsoft/TypeScript/issues/39026)
//when a discriminat in a discriminated union is isolated via deconstruction
export const ChoiceGroup: React.FC<ChoiceGroupProps> = ({
    label,
    onChange,
    selectedKey,
    textAlignment = "left",
    operationData,
    isSingleColumn,
    ...otherProps
}) => {
    useComponentLogger("ChoiceGroup", otherProps.id);

    const [selectedButtonKey, setSelectedButtonKey] = useState(selectedKey || undefined);

    useEffect(() => {
        setSelectedButtonKey(selectedKey);
    }, [selectedKey]);

    const viewportType = useViewport();
    const theme = useTheme();
    const isMobile = isMobileViewport(viewportType);

    const isSelectOrMedia = ["selectButton", "mediaButton"].includes(otherProps.optionType);

    const interimOnChange = (ev?: React.FormEvent<HTMLElement | HTMLInputElement>, option?: IChoiceGroupOption) => {
        //In FluentUI implementation, you should not be able to automatically select other items
        //in choicegroup unless an explicit onChange function is provided, so in order to match that functionality
        //we must not do anything if a selectedKey is provided
        if (isSelectOrMedia && !selectedKey) {
            setSelectedButtonKey(option!.key);
        }
        if (onChange) {
            onChange(option! as ChoiceGroupOptionProps, ev);
        }
    };

    const getComputedOptions = (disabled?: boolean): IChoiceGroupOption[] => {
        let computedOptions: IChoiceGroupOption[];

        if (otherProps.optionType === "selectButton") {
            computedOptions = otherProps.options.map((option) => (
                {
                    ...option,
                    styles: getChoiceGroupOptionStyles(theme, isMobile, option.key === selectedButtonKey, disabled ? disabled : option.disabled, isSingleColumn),
                    onRenderField: () => (
                        <SelectButton
                            {...option}
                            height={isSingleColumn ? "auto" : otherProps.height}
                            textAlignment={textAlignment}
                            selected={option.key === selectedButtonKey}
                            disabled={disabled ? disabled : option.disabled}
                            optionType={otherProps.optionType}
                        />
                    )
                }
            ));
        } else if (otherProps.optionType === "mediaButton") {
            computedOptions = otherProps.options.map((option) => (
                {
                    ...option,
                    styles: getChoiceGroupOptionStyles(theme, isMobile, option.key === selectedButtonKey, disabled ? disabled : option.disabled, isSingleColumn),
                    onRenderField: () => (
                        <MediaButton
                            {...option}
                            height={isSingleColumn ? "auto" : otherProps.height}
                            mediaPosition={otherProps.mediaPosition ?? "left"}
                            selected={option.key === selectedButtonKey}
                            disabled={disabled ? disabled : option.disabled}
                            optionType={otherProps.optionType}
                        />
                    )
                }
            ));
        } else {
            computedOptions = otherProps.options.slice();
        }

        return computedOptions;
    };

    //Taking out properties we've already used in otherProps so that they don't
    //get used in Fluent
    const { optionType, options, height, mediaPosition, ...passedProps } = otherProps;
    validateProps("ChoiceGroup", passedProps);

    const choiceGroupProps: IChoiceGroupProps = {
        label,
        selectedKey,
        options: getComputedOptions(),
        onChange: interimOnChange,
        styles: getContainerStyles(optionType),
        ...passedProps
    };

    if (operationData) {

        return (
            <TwoUIProgressView
                operationData={operationData.progressViewOperationData}
                updatingView={(
                    <>
                        <Stack horizontal={true} childrenGap={8}>
                            <Label disabled={true}>{label}</Label>
                            <UpdatingLoadingSpinner />
                        </Stack>
                        <FluentChoiceGroup
                            {...choiceGroupProps}
                            label=""
                            disabled={true}
                            options={getComputedOptions(true)}
                        />
                    </>
                )}
                updateFailedView={(
                    <>
                        <Stack horizontal={true} childrenGap={8}>
                            <Label>{label}</Label>
                            <Stack padding="8px 0px 0px 0px">
                                <ErrorCallout {...operationData.errorProps} />
                            </Stack>
                        </Stack>
                        <FluentChoiceGroup
                            {...choiceGroupProps}
                            label=""
                        />
                    </>
                )}
                finishedView={(
                    <FluentChoiceGroup {...choiceGroupProps} />
                )}

            />
        );
    } else {
        return (
            <FluentChoiceGroup {...choiceGroupProps} />
        );
    }

};
